/**
 * Update date: 26-05-2023
 * Screen 2042.5
 */
import { useRef } from 'react';
import { useCustomContext } from '../store';
import { DataGridFull } from 'app/components/DataGrid';
import LoadPanel from 'app/components/LoadPanel';
import moment from 'moment';
import { TAKEOUT_STATUS } from 'constant';
import useFormat from 'hooks/useFormat';
import useFormatTime from 'hooks/useFormatTime';
import useDictionary from 'hooks/useDictionary';
import { useApp } from 'app';

const exportFileName =
  'SMDC_2042.5 Carryout Detais Inquiry_' + moment().format('YYYYMMDD');

function List(props) {
  const { themePro }: any = useApp();
  const { t }: any = useDictionary({
    programId: '2042.5',
  });
  // context
  const { gridData, isLoading } = useCustomContext();

  //ref
  const dataRef: any = useRef(null);

  const { DateFormat, DateTimeFormat } = useFormatTime();
  const { QtyFormat, AmountFormat, DoubleFormat } = useFormat();

  const columns: any = [
    {
      caption: t('Takeout'),
      alignment: 'center',
      columns: [
        {
          dataField: 'billId',
          caption: t('Bill ID'),
          alignment: themePro ? 'left' : 'center',
          showWhenGrouped: true,
        },
        {
          dataField: 'date',
          caption: t('Date'),
          dataType: 'date',
          format: DateFormat,
          showWhenGrouped: true,
        },
        {
          dataField: 'vendorName',
          caption: t('Vendor'),
          showWhenGrouped: true,
        },
        {
          dataField: 'zoneName',
          caption: t('Zone'),
          showWhenGrouped: true,
        },
        {
          dataField: 'takeoutStateName',
          caption: t('Takeout State'),
          showWhenGrouped: true,
        },
        {
          dataField: 'status',
          caption: t('Takeout Status'),
          showWhenGrouped: true,
          customizeText: cell =>
            TAKEOUT_STATUS.find(item => item.value == cell.value)?.text,
        },
      ],
    },
    {
      caption: t('Product'),
      alignment: 'center',
      columns: [
        {
          dataField: 'productCode',
          caption: t('Product Code'),
          showWhenGrouped: true,
        },
        {
          dataField: 'productName',
          caption: t('Product Name'),
          showWhenGrouped: true,
        },
        {
          dataField: 'marketableSize',
          caption: t('Marketable Size'),
          showWhenGrouped: true,
        },
        {
          dataField: 'optionCode',
          caption: t('Option Code'),
          showWhenGrouped: true,
        },
        {
          dataField: 'optionName',
          caption: t('Option'),
          alignment: themePro ? 'left' : 'center',
          showWhenGrouped: true,
        },
        {
          dataField: 'quantityPerPack',
          caption: t('Quantity Per Pack'),
          showWhenGrouped: true,
          format: QtyFormat,
          dataType: 'number',
        },
        {
          dataField: 'purchaseUnitPrice',
          caption: t('Purchase Unit Price'),
          format: DoubleFormat,
          dataType: 'number',
          showWhenGrouped: true,
        },
      ],
    },
    {
      caption: t('Content'),
      alignment: 'center',
      columns: [
        {
          dataField: 'quantity',
          caption: t('QTY'),
          format: QtyFormat,
          dataType: 'number',
          showWhenGrouped: true,
        },
        {
          dataField: 'ea',
          caption: t('EA'),
          format: QtyFormat,
          dataType: 'number',
          showWhenGrouped: true,
        },
        {
          dataField: 'amount',
          caption: t('Amount'),
          format: AmountFormat,
          dataType: 'number',
          showWhenGrouped: true,
        },
        {
          dataField: 'supplyAmount',
          caption: t('Supply Amount'),
          format: AmountFormat,
          dataType: 'number',
          showWhenGrouped: true,
        },
        {
          dataField: 'vat',
          caption: t('VAT'),
          showWhenGrouped: true,
          format: AmountFormat,
          dataType: 'number',
        },
        {
          dataField: 'bottle',
          caption: t('Bottle'),
          format: AmountFormat,
          dataType: 'number',
          showWhenGrouped: true,
        },
        {
          dataField: 'container',
          caption: t('Container'),
          format: AmountFormat,
          dataType: 'number',
          showWhenGrouped: true,
        },
        {
          dataField: 'gtotal',
          caption: t('G. Total'),
          format: AmountFormat,
          dataType: 'number',
          showWhenGrouped: true,
        },
      ],
    },
    {
      dataField: 'billNote',
      caption: t('Bill Note'),
      visible: false,
      showWhenGrouped: true,
    },
    {
      dataField: 'lineNote',
      caption: t('Line Note'),
      visible: false,
      showWhenGrouped: true,
    },
    {
      dataField: '',
      caption: t('Receive Due Bill ID'),
      visible: false,
      showWhenGrouped: true,
    },
    {
      dataField: 'bottleId',
      caption: t('Bottle ID'),
      visible: false,
      showWhenGrouped: true,
    },
    {
      dataField: 'bottleUnitPrice',
      caption: t('Bottle Unit Price'),
      format: AmountFormat,
      dataType: 'number',
      visible: false,
      showWhenGrouped: true,
    },
    {
      dataField: 'containerId',
      caption: t('Container ID'),
      visible: false,
      showWhenGrouped: true,
    },
    {
      dataField: 'containerUnitPrice',
      caption: t('Container Unit Price'),
      format: AmountFormat,
      dataType: 'number',
      visible: false,
      showWhenGrouped: true,
    },
    {
      dataField: 'markerName',
      caption: t('Maker'),
      visible: false,
      showWhenGrouped: true,
    },
    {
      dataField: 'originName',
      caption: t('Origin'),
      visible: false,
      showWhenGrouped: true,
    },
    {
      dataField: 'defaultVendorName',
      caption: t('Default Vendor'),
      visible: false,
      showWhenGrouped: true,
    },
    {
      dataField: 'largeCategoryName',
      caption: t('Large category'),
      visible: false,
      showWhenGrouped: true,
    },
    {
      dataField: 'mediumCategoryName',
      caption: t('Medium category'),
      visible: false,
      showWhenGrouped: true,
    },
    {
      dataField: 'smallCategoryName',
      caption: t('Small category'),
      visible: false,
      showWhenGrouped: true,
    },
    {
      dataField: 'subCategoryName',
      caption: t('Sub category'),
      visible: false,
      showWhenGrouped: true,
    },
    {
      dataField: 'registerDateTime',
      caption: t('Register Date&Time'),
      dataType: 'date',
      format: DateTimeFormat,
      visible: false,
      showWhenGrouped: true,
    },
    {
      dataField: 'registerUserName',
      caption: t('Register User'),
      visible: false,
      showWhenGrouped: true,
    },
    {
      dataField: 'registerProgramId',
      caption: t('Register Program ID'),
      visible: false,
      showWhenGrouped: true,
    },
    {
      dataField: 'modifyDateTime',
      caption: t('Modify Date&Time'),
      dataType: 'date',
      format: DateTimeFormat,
      visible: false,
      showWhenGrouped: true,
    },
    {
      dataField: 'modifyUserName',
      caption: t('Modify User'),
      visible: false,
      showWhenGrouped: true,
    },
    {
      dataField: 'modifyProgramId',
      caption: t('Modify Program ID'),
      visible: false,
      showWhenGrouped: true,
    },
  ];

  /**
   * on export excel
   *
   */
  const exportExcel = () => {
    dataRef?.current?.instance?.exportToExcel();
  };

  return (
    <>
      <LoadPanel visible={isLoading} />
      <DataGridFull
        storageKeyInquiry
        columns={columns}
        ref={dataRef}
        dataSource={gridData}
        storageKey="CarryoutInquiry"
        options={{
          groupPanel: {
            visible: true,
          },
          columnAutoWidth: true,
          onToolbarPreparing: (e: any) => {
            e.toolbarOptions.items.unshift(
              {
                location: 'before',
                template: 'totalCount',
              },
              {
                location: 'before',
                widget: 'dxButton',
                options: {
                  icon: 'xlsxfile',
                  text: t('Export Excel'),
                  disabled: !gridData.length,
                  onClick: () => exportExcel(),
                },
              },
            );
          },
          export: {
            fileName: exportFileName,
          },
          summary: {
            totalItems: [
              {
                name: 'QTY',
                column: 'quantity',
                valueFormat: QtyFormat,
                displayFormat: '{0}',
                dataType: 'number',
                summaryType: 'sum',
              },
              {
                name: 'EA',
                column: 'ea',
                valueFormat: QtyFormat,
                displayFormat: '{0}',
                dataType: 'number',
                summaryType: 'sum',
              },
              {
                name: 'Amount',
                column: 'amount',
                valueFormat: AmountFormat,
                displayFormat: '{0}',
                dataType: 'number',
                summaryType: 'sum',
              },
              {
                name: 'Supply Amount',
                column: 'supplyAmount',
                valueFormat: AmountFormat,
                displayFormat: '{0}',
                dataType: 'number',
                summaryType: 'sum',
              },
              {
                name: 'VAT',
                column: 'vat',
                valueFormat: AmountFormat,
                displayFormat: '{0}',
                dataType: 'number',
                summaryType: 'sum',
              },
              {
                name: 'Bottle',
                column: 'bottle',
                valueFormat: AmountFormat,
                displayFormat: '{0}',
                dataType: 'number',
                summaryType: 'sum',
              },
              {
                name: 'Container',
                column: 'container',
                valueFormat: AmountFormat,
                displayFormat: '{0}',
                dataType: 'number',
                summaryType: 'sum',
              },
              {
                name: 'G. Total',
                column: 'gtotal',
                valueFormat: AmountFormat,
                displayFormat: '{0}',
                dataType: 'number',
                summaryType: 'sum',
              },
            ],
            groupItems: [
              {
                summaryType: 'count',
                displayFormat: '{0}',
              },
              // {
              //   column: 'billId',
              //   customizeText: 'Sum',
              // },
              {
                column: 'ea',
                summaryType: 'sum',
                alignByColumn: true,
                showInGroupFooter: false,
                valueFormat: QtyFormat,
                displayFormat: '{0}',
                dataType: 'number',
              },
              {
                column: 'amount',
                summaryType: 'sum',
                alignByColumn: true,
                showInGroupFooter: false,
                valueFormat: AmountFormat,
                displayFormat: '{0}',
                dataType: 'number',
              },
              {
                column: 'supplyAmount',
                summaryType: 'sum',
                alignByColumn: true,
                showInGroupFooter: false,
                valueFormat: AmountFormat,
                displayFormat: '{0}',
                dataType: 'number',
              },
              {
                column: 'vat',
                summaryType: 'sum',
                alignByColumn: true,
                showInGroupFooter: false,
                valueFormat: AmountFormat,
                displayFormat: '{0}',
                dataType: 'number',
              },
              {
                column: 'bottle',
                summaryType: 'sum',
                alignByColumn: true,
                showInGroupFooter: false,
                valueFormat: AmountFormat,
                displayFormat: '{0}',
                dataType: 'number',
              },
              {
                column: 'container',
                summaryType: 'sum',
                alignByColumn: true,
                showInGroupFooter: false,
                valueFormat: AmountFormat,
                displayFormat: '{0}',
                dataType: 'number',
              },
              {
                column: 'gtotal',
                summaryType: 'sum',
                alignByColumn: true,
                showInGroupFooter: false,
                valueFormat: AmountFormat,
                displayFormat: '{0}',
                dataType: 'number',
              },
            ],
          },
        }}
      />
    </>
  );
}

export default List;
